/**
 * User API Custom Hook
 * 
 * @description
 * User 관련 API를 호출하는 Custom Hook
 * 
 * @phase Phase 8 (API 연동)
 * 
 * @features
 * - 회원가입 (POST /user/signup)
 * - 로그인 ID 중복 확인 (POST /user/check-login-id)
 * - 이메일 중복 확인 (POST /user/check-email)
 * - 로그인 (POST /user/login)
 * - 프로필 조회 (GET /user/profile)
 * - 프로필 수정 (PATCH /user/profile)
 * - 회원 탈퇴 (DELETE /user/my-account)
 * 
 * @usage
 * const { signup, checkLoginId, login, getProfile, updateProfile, deleteAccount } = useUser();
 * 
 * @api
 * Base URL: process.env.REACT_APP_API_BASE_URL
 * 인증 필요: getProfile, updateProfile, deleteAccount
 */

import {
	SignupRequestDto,
	SignupResponseDto,
	CheckLoginIdRequestDto,
	CheckLoginIdResponseDto,
	CheckEmailRequestDto,
	CheckEmailResponseDto,
	LoginDto,
	LoginResponseDto,
	UserProfileResponseDto,
	UpdateUserProfileRequestDto,
	UpdateUserProfileResponseDto,
	DeleteMyAccountResponseDto,
	ChangeMyPasswordRequestDto,
	ChangeMyPasswordResponseDto,
} from '../../types/user';

import apiClient, { ApiError } from '../../services/apiClient';
import { fetchWithSessionRefresh } from '../../services/authSession';
import type { DeleteMyAccountRequestDto } from '../../types/api/auth';
import { normalizeBearerTokenForAuthorization } from '../../utils/bearerTokenNormalize';
import i18n from '../../i18n';

function readStoredAccessToken(): string | null {
	const raw = localStorage.getItem('medicrew_access_token');
	if (raw == null || raw === '') return null;
	return normalizeBearerTokenForAuthorization(raw);
}

// REACT_APP_API_BASE_URL이 빈 문자열이면 상대경로 사용 (devServer proxy 경유)
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL !== undefined && process.env.REACT_APP_API_BASE_URL !== ''
    ? process.env.REACT_APP_API_BASE_URL
    : '';

function isAbsoluteHttpOrHttpsUrl(url: string): boolean {
	try {
		const u = new URL(url);
		return u.protocol === 'http:' || u.protocol === 'https:';
	} catch {
		return false;
	}
}

/**
 * PATCH /user/profile 본문 정리.
 * - CRA 번들 아바타(/static/media/…) 등 상대 URL은 서버 @URL 검증에서 400이 나는 경우가 많아 제외.
 * - 전화번호는 숫자만 보내는 백엔드가 많아, 형식이 맞으면 숫자만 전송.
 */
function sanitizeUpdateProfileRequestDto(data: UpdateUserProfileRequestDto): UpdateUserProfileRequestDto {
	const out: UpdateUserProfileRequestDto = {};
	if (data.realName !== undefined) {
		const v = data.realName.trim();
		if (v) out.realName = v;
	}
	if (data.phoneNumber !== undefined) {
		const t = data.phoneNumber.trim();
		const digits = t.replace(/\D/g, '');
		out.phoneNumber = digits.length >= 8 && digits.length <= 15 ? digits : t;
	}
	if (data.position !== undefined) {
		out.position = data.position.trim();
	}
	if (data.affiliation !== undefined) {
		out.affiliation = data.affiliation.trim();
	}
	if (data.jobTitle !== undefined) {
		out.jobTitle = data.jobTitle;
	}
	if (data.customJobTitle !== undefined) {
		out.customJobTitle = data.customJobTitle.trim();
	}
	if (data.language !== undefined) {
		const v = data.language.trim().toLowerCase().split('-')[0];
		if (v) out.language = v.length <= 16 ? v : v.slice(0, 16);
	}
	if (data.countryId !== undefined) {
		out.countryId = data.countryId;
	}
	if (data.profileImageUrl !== undefined) {
		const u = data.profileImageUrl.trim();
		if (u && isAbsoluteHttpOrHttpsUrl(u)) out.profileImageUrl = u;
	}
	return out;
}

async function readHttpErrorDetail(response: Response): Promise<string> {
	const text = await response.text();
	if (!text.trim()) return '';
	try {
		const j = JSON.parse(text) as Record<string, unknown>;
		if (typeof j.message === 'string' && j.message.trim()) return j.message.trim();
		if (Array.isArray(j.message)) return j.message.map(String).join(', ');
		if (typeof j.error === 'string' && j.error.trim()) return j.error.trim();
		const errs = j.errors;
		if (Array.isArray(errs)) {
			const parts = errs.map((e: unknown) => {
				if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
					return (e as { message: string }).message;
				}
				if (e && typeof e === 'object' && 'defaultMessage' in e && typeof (e as { defaultMessage: unknown }).defaultMessage === 'string') {
					return (e as { defaultMessage: string }).defaultMessage;
				}
				return String(e);
			});
			return parts.filter(Boolean).join('; ');
		}
	} catch {
		return text.trim().slice(0, 500);
	}
	return text.trim().slice(0, 500);
}

/**
 * User API Custom Hook
 * 
 * @returns User API 함수들
 */
export const useUser = () => {
	/**
	 * 회원가입 API
	 * 
	 * @description
	 * POST /user/signup
	 * 새로운 회원을 등록합니다.
	 * 
	 * @param data - 회원가입 요청 데이터
	 * @returns Promise<SignupResponseDto> - 생성된 유저 ID
	 * 
	 * @throws
	 * - 400: 필수 약관에 모두 동의해야 합니다.
	 * - 404: 존재하지 않는 약관이 포함되어 있습니다.
	 * - 409: 이미 사용중인 loginId입니다.
	 * 
	 * @example
	 * const result = await signup({
	 *   loginId: 'testuser',
	 *   pw: 'password123',
	 *   realName: 'John Smith',
	 *   termsIdList: [1, 2]
	 * });
	 */
	const signup = async (data: SignupRequestDto): Promise<SignupResponseDto> => {
		const response = await fetch(`${API_BASE_URL}/user/signup`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		});

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(errorText || `회원가입 실패: ${response.status}`);
		}

		return await response.json();
	};

	/**
	 * 로그인 ID 중복 확인 API
	 * 
	 * @description
	 * POST /user/check-login-id
	 * 로그인 ID가 사용 가능한지 확인합니다.
	 * 
	 * @param loginId - 확인할 로그인 ID
	 * @returns Promise<CheckLoginIdResponseDto> - { isAvailable: boolean }
	 * 
	 * @example
	 * const result = await checkLoginId('testuser');
	 * if (result.isAvailable) {
	 *   // ID 사용 가능
	 * }
	 */
	const checkLoginId = async (loginId: string): Promise<CheckLoginIdResponseDto> => {
		const response = await fetch(`${API_BASE_URL}/user/check-login-id`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ loginId } as CheckLoginIdRequestDto),
		});

		if (!response.ok) {
			throw new Error(`로그인 ID 중복 확인 실패: ${response.status}`);
		}

		return await response.json();
	};

	/**
	 * 이메일 중복 확인 API
	 * 
	 * @description
	 * POST /user/check-email
	 * 이메일이 사용 가능한지 확인합니다.
	 * 
	 * @param email - 확인할 이메일 주소
	 * @returns Promise<CheckEmailResponseDto> - { isAvailable: boolean }
	 * 
	 * @example
	 * const result = await checkEmail('user@example.com');
	 * if (result.isAvailable) {
	 *   // 이메일 사용 가능
	 * }
	 */
	const checkEmail = async (email: string): Promise<CheckEmailResponseDto> => {
		const response = await fetch(`${API_BASE_URL}/user/check-email`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ email } as CheckEmailRequestDto),
		});

		if (!response.ok) {
			throw new Error(`이메일 중복 확인 실패: ${response.status}`);
		}

		return await response.json();
	};

	/**
	 * 로그인 API
	 * 
	 * @description
	 * POST /user/login
	 * 사용자 인증 후 액세스 토큰을 발급합니다.
	 * 
	 * @param data - 로그인 요청 데이터 (loginId, password)
	 * @returns Promise<LoginResponseDto> - { accessToken, refreshToken }
	 * 
	 * @throws
	 * - 404: 유효하지 않는 계정정보입니다.
	 * - 409: 비활성 상태의 계정입니다.
	 * 
	 * @example
	 * const result = await login({ loginId: 'testuser', password: 'password123' });
	 * localStorage.setItem('accessToken', result.accessToken);
	 * localStorage.setItem('refreshToken', result.refreshToken);
	 */
	const login = async (data: LoginDto): Promise<LoginResponseDto> => {
		const response = await fetch(`${API_BASE_URL}/user/login`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		});

		if (!response.ok) {
			if (response.status === 404) {
				throw new Error(i18n.t('auth:alertInvalidCredentials'));
			} else if (response.status === 409) {
				throw new Error(i18n.t('auth:alertInactiveAccount'));
			}
			throw new Error(i18n.t('auth:alertLoginError'));
		}

		return await response.json();
	};

	/**
	 * 유저 프로필 조회 API
	 * 
	 * @description
	 * GET /user/profile
	 * 현재 로그인한 사용자의 프로필 정보를 조회합니다.
	 * 
	 * @returns Promise<UserProfileResponseDto> - 유저 프로필 정보
	 * 
	 * @throws
	 * - 401: 인증이 필요합니다.
	 * - 404: 존재하지 않는 유저입니다.
	 * 
	 * @auth Bearer Token 필요
	 * 
	 * @example
	 * const profile = await getProfile();
	 * // profile.realName 예: "John Smith"
	 */
	const getProfile = async (): Promise<UserProfileResponseDto> => {
		const accessToken = readStoredAccessToken();

		if (!accessToken) {
			throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
		}

		// 401 이면 토큰 재발급 후 1회 재시도 (재시도 시 최신 토큰 사용)
		const response = await fetchWithSessionRefresh(() =>
			fetch(`${API_BASE_URL}/user/profile`, {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${readStoredAccessToken()}`,
				},
			})
		);

		if (!response.ok) {
			if (response.status === 401) {
				throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
			} else if (response.status === 404) {
				throw new Error('존재하지 않는 유저입니다.');
			}
			throw new Error(`프로필 조회 실패: ${response.status}`);
		}

		const data = await response.json();
		
		// 백엔드가 id를 반환하지만 타입 정의는 userId이므로 매핑
		if (data.id !== undefined && data.userId === undefined) {
			data.userId = data.id;
		}

		return data;
	};

	/**
	 * 유저 프로필 수정 API
	 * 
	 * @description
	 * PATCH /user/profile
	 * 현재 로그인한 사용자의 프로필 정보를 수정합니다.
	 * 
	 * @param data - 수정할 프로필 데이터 (realName, profileImageUrl)
	 * @returns Promise<UpdateUserProfileResponseDto> - { message: string }
	 * 
	 * @throws
	 * - 401: 인증이 필요합니다.
	 * - 404: 존재하지 않는 유저입니다.
	 * 
	 * @auth Bearer Token 필요
	 * 
	 * @example
	 * const result = await updateProfile({
	 *   realName: 'John Smith',
	 *   profileImageUrl: 'https://example.com/profile.jpg'
	 * });
	 */
	const updateProfile = async (data: UpdateUserProfileRequestDto): Promise<UpdateUserProfileResponseDto> => {
		const accessToken = readStoredAccessToken();

		if (!accessToken) {
			throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
		}

		const payload = sanitizeUpdateProfileRequestDto(data);
		const requestUpdateProfile = (body: UpdateUserProfileRequestDto) =>
			fetchWithSessionRefresh(() =>
				fetch(`${API_BASE_URL}/user/profile`, {
					method: 'PATCH',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${readStoredAccessToken()}`,
					},
					body: JSON.stringify(body),
				})
			);

		let response = await requestUpdateProfile(payload);

		if (!response.ok) {
			const detail = await readHttpErrorDetail(response);
			if (response.status === 400 && 'countryId' in payload) {
				const payloadWithoutCountryId: UpdateUserProfileRequestDto = { ...payload };
				delete payloadWithoutCountryId.countryId;
				response = await requestUpdateProfile(payloadWithoutCountryId);
				if (response.ok) {
					return await response.json();
				}
			}
			if (response.status === 401) {
				throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
			} else if (response.status === 404) {
				throw new Error('존재하지 않는 유저입니다.');
			}
			const suffix = detail ? `: ${detail}` : '';
			throw new Error(`프로필 수정 실패 (${response.status})${suffix}`);
		}

		const json = await response.json();
		return json;
	};

	/**
	 * 회원 탈퇴 API
	 * 
	 * @description
	 * DELETE /user/my-account
	 * 현재 로그인한 사용자의 계정을 삭제합니다.
	 * 
	 * @returns Promise<DeleteMyAccountResponseDto> - { message: string }
	 * 
	 * @throws
	 * - 401: 인증이 필요합니다.
	 * - 404: 존재하지 않는 유저입니다.
	 * 
	 * @auth Bearer Token 필요
	 * 
	 * @important
	 * ⚠️ 실제 계정 삭제가 진행됩니다. 주의해서 사용하세요.
	 * 
	 * @example
	 * const result = await deleteAccount();
	 * // result.message 예: "회원 탈퇴가 완료되었습니다."
	 * // LocalStorage 정리
	 * localStorage.removeItem('accessToken');
	 * localStorage.removeItem('refreshToken');
	 */
	const deleteAccount = async (feedback?: string): Promise<DeleteMyAccountResponseDto> => {
		try {
			const body: DeleteMyAccountRequestDto = feedback ? { feedback } : {};
			return await apiClient.delete<DeleteMyAccountResponseDto>('/user/my-account', body);
		} catch (error) {
			if (error instanceof ApiError) {
				if (error.status === 401) throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
				if (error.status === 404) throw new Error('존재하지 않는 유저입니다.');
			}
			throw error instanceof Error ? error : new Error('회원 탈퇴 중 오류가 발생했습니다.');
		}
	};

	/**
	 * 내 비밀번호 변경 API
	 * 
	 * @description
	 * PATCH /user/my-password
	 * 현재 로그인한 사용자가 자신의 비밀번호를 변경합니다.
	 * 
	 * @param data - 비밀번호 변경 요청 데이터 (currentPassword, newPassword)
	 * @returns Promise<ChangeMyPasswordResponseDto> - { message: string }
	 * 
	 * @throws
	 * - 401: 인증이 만료되었습니다.
	 * - 404: 사용자를 찾을 수 없습니다.
	 * - 400: 현재 비밀번호가 일치하지 않습니다.
	 * 
	 * @auth Bearer Token 필요
	 * 
	 * @example
	 * const result = await changeMyPassword({
	 *   currentPassword: 'oldPassword123',
	 *   newPassword: 'newPassword456'
	 * });
	 */
	const changeMyPassword = async (data: ChangeMyPasswordRequestDto): Promise<ChangeMyPasswordResponseDto> => {
		try {
			return await apiClient.patch<ChangeMyPasswordResponseDto>('/user/my-password', data);
		} catch (error) {
			if (error instanceof ApiError) {
				if (error.status === 401) throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
				if (error.status === 404) throw new Error('사용자를 찾을 수 없습니다.');
				if (error.status === 400) throw new Error('현재 비밀번호가 일치하지 않습니다.');
			}
			throw error instanceof Error ? error : new Error('비밀번호 변경 중 오류가 발생했습니다.');
		}
	};

	return {
		signup,
		checkLoginId,
		checkEmail,
		login,
		getProfile,
		updateProfile,
		deleteAccount,
		changeMyPassword,
	};
};